<template>
  <div class="avatar-page">
    <section class="avatar-hero">
      <div class="avatar-hero-copy">
        <p class="avatar-kicker">Microsoft Speech Avatar</p>
        <h1 class="avatar-title">数字人</h1>
        <p class="avatar-subtitle">
          基于微软官方实时 Avatar 能力，通过 WebRTC 建立会话后，让数字人直接播报输入文本。
        </p>
        <div class="avatar-hero-actions">
          <el-button type="primary" icon="el-icon-video-play" :loading="startingSession" @click="startSession">
            {{ sessionActive ? '重新连接会话' : '建立会话' }}
          </el-button>
          <el-button icon="el-icon-microphone" :loading="speaking" @click="speakText">
            让数字人说话
          </el-button>
          <el-button icon="el-icon-switch-button" :disabled="!sessionReady" @click="stopSession">
            关闭会话
          </el-button>
        </div>
      </div>

      <div class="avatar-hero-side">
        <div class="hero-card">
          <div class="hero-card-top">
            <span class="hero-badge" :class="{ ok: serviceReady }">
              {{ serviceReady ? '服务已配置' : '服务未配置' }}
            </span>
            <span class="hero-meta">{{ serviceRegion || '等待读取区域' }}</span>
          </div>
          <div class="hero-status-row">
            <span class="hero-status-label">当前状态</span>
            <strong>{{ statusText }}</strong>
          </div>
          <div class="hero-status-row">
            <span class="hero-status-label">凭据来源</span>
            <strong>{{ tokenSourceLabel }}</strong>
          </div>
          <div class="hero-status-row">
            <span class="hero-status-label">会话模式</span>
            <strong>实时 WebRTC</strong>
          </div>
        </div>
      </div>
    </section>

    <section class="avatar-layout">
      <div class="avatar-panel control-panel">
        <div class="panel-header">
          <h2>配置</h2>
          <p>先建立会话，再驱动数字人播报文本。</p>
        </div>

        <el-alert
          title="接入说明"
          type="info"
          :closable="false"
          show-icon
          class="control-alert"
        >
          <div class="alert-copy">
            页面按微软官方实时 Avatar 文档实现，浏览器需支持 WebRTC，生产环境建议使用 HTTPS。
          </div>
        </el-alert>

        <div class="field-grid">
          <div class="field">
            <label>数字人角色</label>
            <el-select v-model="form.character" filterable>
              <el-option
                v-for="item in avatarCharacters"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </div>

          <div class="field">
            <label>人物风格</label>
            <el-select v-model="form.style">
              <el-option
                v-for="item in currentStyles"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </div>

          <div class="field">
            <label>发音人</label>
            <el-select v-model="form.voice" filterable>
              <el-option
                v-for="item in voiceOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </div>

          <div class="field">
            <label>背景色</label>
            <div class="color-row">
              <el-color-picker v-model="form.backgroundColor" />
              <el-input v-model="form.backgroundColor" />
            </div>
          </div>
        </div>

        <div class="field">
          <label>背景图 URL（可选）</label>
          <el-input
            v-model.trim="form.backgroundImage"
            placeholder="留空则使用纯色背景，填写公开图片链接则尝试作为数字人背景"
          />
        </div>

        <div class="field">
          <label>播报文本</label>
          <el-input
            v-model="form.text"
            type="textarea"
            :rows="8"
            maxlength="600"
            show-word-limit
            placeholder="输入要让数字人播报的文本。首次测试建议先用一两句话确认语音、角色和网络正常。"
          />
        </div>

        <div class="field quick-preset-field">
          <label>快捷示例</label>
          <div class="quick-preset-list">
            <button
              v-for="preset in textPresets"
              :key="preset.label"
              type="button"
              class="quick-preset"
              @click="applyPreset(preset)"
            >
              {{ preset.label }}
            </button>
          </div>
        </div>

        <div class="notes-card">
          <h3>官方文档要点</h3>
          <ul>
            <li>实时 Avatar 依赖 WebRTC，会话需要先申请 relay token。</li>
            <li>文档说明空闲 5 分钟会自动断开，单次连接最长约 30 分钟。</li>
            <li>当前页面默认从 Azure_digital_human 表读取 AccessToken 和 area，不在浏览器暴露 Azure key。</li>
          </ul>
          <div class="doc-links">
            <a
              href="https://learn.microsoft.com/zh-cn/azure/ai-services/speech-service/text-to-speech-avatar/what-is-text-to-speech-avatar"
              target="_blank"
              rel="noopener noreferrer"
            >
              什么是文本转语音 Avatar
            </a>
            <a
              href="https://learn.microsoft.com/zh-cn/azure/ai-services/speech-service/text-to-speech-avatar/real-time-synthesis-avatar"
              target="_blank"
              rel="noopener noreferrer"
            >
              实时 Avatar 接入文档
            </a>
          </div>
        </div>
      </div>

      <div class="avatar-panel preview-panel">
        <div class="panel-header">
          <h2>预览</h2>
          <p>视频轨和音频轨都来自 Avatar 实时会话。</p>
        </div>

        <div class="stage-shell" :class="{ ready: sessionReady }">
          <div class="stage-toolbar">
            <span class="stage-pill">{{ sessionReady ? '会话已连接' : '等待会话' }}</span>
            <span class="stage-pill subtle">{{ currentAvatarLabel }}</span>
            <span class="stage-pill subtle">{{ currentVoiceLabel }}</span>
          </div>
          <div class="stage">
            <video ref="avatarVideo" class="avatar-video" playsinline autoplay></video>
            <audio ref="avatarAudio" class="avatar-audio" autoplay></audio>
            <div v-if="!hasRemoteVideo" class="stage-placeholder">
              <div class="placeholder-card" :style="placeholderCardStyle">
                <div class="placeholder-avatar-shell">
                  <img :src="currentAvatarPreview.image" :alt="currentAvatarPreview.label" class="placeholder-avatar-image">
                </div>
                <p class="placeholder-title">{{ currentAvatarPreview.label }}</p>
                <p class="placeholder-style">{{ currentStyleLabel }}</p>
                <p class="placeholder-text">
                  你切换角色后，这里会先实时更新当前数字人的形象预览；建立会话后会切到真实 Avatar 视频流。
                </p>
                <p v-if="sessionActive && !startingSession" class="placeholder-tip">
                  已检测到当前角色配置，可重新连接以加载对应视频形象。
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="status-card">
          <div class="status-row">
            <span>状态</span>
            <strong>{{ statusText }}</strong>
          </div>
          <div class="status-row">
            <span>ICE</span>
            <strong>{{ iceState }}</strong>
          </div>
          <div class="status-row">
            <span>PeerConnection</span>
            <strong>{{ peerState }}</strong>
          </div>
          <div class="status-row">
            <span>最近一次错误</span>
            <strong>{{ lastError || '无' }}</strong>
          </div>
        </div>

        <div class="log-card">
          <div class="log-card-top">
            <h3>会话日志</h3>
            <el-button size="mini" @click="clearLogs">清空</el-button>
          </div>
          <div class="log-list">
            <div v-for="item in logs" :key="item.id" class="log-item" :data-type="item.type">
              <span class="log-time">{{ item.time }}</span>
              <span class="log-message">{{ item.message }}</span>
            </div>
            <p v-if="!logs.length" class="log-empty">还没有日志，先建立一次会话。</p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
const STYLE_LABELS = {
  'casual-sitting': '休闲坐姿',
  'graceful-sitting': '优雅坐姿',
  'graceful-standing': '优雅站姿',
  'technical-sitting': '技术讲解坐姿',
  'technical-standing': '技术讲解站姿',
  casual: '休闲形象',
  formal: '正式形象',
  graceful: '优雅形象',
  business: '商务形象',
  youthful: '青春形象'
}

const AVATAR_CHARACTERS = [
  {
    value: 'lisa',
    label: 'Lisa',
    chineseLabel: '丽莎',
    image: require('@/assets/female.png'),
    accent: '#6aa8ff',
    styles: ['casual-sitting', 'graceful-sitting', 'graceful-standing', 'technical-sitting', 'technical-standing']
  },
  {
    value: 'lori',
    label: 'Lori',
    chineseLabel: '洛莉',
    image: require('@/assets/female.png'),
    accent: '#ff8fa3',
    styles: ['casual', 'graceful', 'formal']
  },
  {
    value: 'meg',
    label: 'Meg',
    chineseLabel: '梅格',
    image: require('@/assets/female.png'),
    accent: '#8c7cff',
    styles: ['formal', 'casual', 'business']
  },
  {
    value: 'max',
    label: 'Max',
    chineseLabel: '马克斯',
    image: require('@/assets/male.png'),
    accent: '#4db6ac',
    styles: ['business', 'casual', 'formal']
  },
  {
    value: 'harry',
    label: 'Harry',
    chineseLabel: '哈里',
    image: require('@/assets/male.png'),
    accent: '#ffb74d',
    styles: ['business', 'casual', 'youthful']
  }
]

const VOICE_OPTIONS = [
  { value: 'zh-CN-XiaoxiaoNeural', label: '晓晓（中文，女声）' },
  { value: 'zh-CN-YunxiNeural', label: '云希（中文，男声）' },
  { value: 'en-US-AvaMultilingualNeural', label: 'Ava Multilingual（多语言，女声）' },
  { value: 'en-US-AndrewMultilingualNeural', label: 'Andrew Multilingual（多语言，男声）' }
]

const TEXT_PRESETS = [
  {
    label: '欢迎词',
    text: '你好，欢迎来到数字人工具演示页面。这一段文本会通过微软实时 Avatar 接口播报。'
  },
  {
    label: '产品介绍',
    text: '本页面会先创建 WebRTC 会话，再由数字人把输入内容转成实时语音和视频输出。'
  },
  {
    label: '中文测试',
    text: '现在进行中文播报测试，请确认你已经看到视频画面并能听到语音输出。'
  }
]

function formatNowTime() {
  const d = new Date()
  const pad = (value) => String(value).padStart(2, '0')
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

export default {
  name: 'DigitalHumanAvatar',
  data() {
    return {
      avatarCharacters: AVATAR_CHARACTERS,
      voiceOptions: VOICE_OPTIONS,
      textPresets: TEXT_PRESETS,
      form: {
        character: 'lisa',
        style: 'casual-sitting',
        voice: 'zh-CN-XiaoxiaoNeural',
        backgroundColor: '#F3F7FF',
        backgroundImage: '',
        text: TEXT_PRESETS[0].text
      },
      serviceRegion: '',
      tokenSource: '',
      statusText: '未初始化',
      iceState: 'new',
      peerState: '未创建',
      lastError: '',
      serviceReady: false,
      sessionReady: false,
      sessionActive: false,
      hasRemoteVideo: false,
      startingSession: false,
      speaking: false,
      peerConnection: null,
      avatarSynthesizer: null,
      sessionRefreshTimer: null,
      sessionPromise: null,
      logs: [],
      logSeq: 0
    }
  },
  computed: {
    currentStyles() {
      const match = this.avatarCharacters.find(item => item.value === this.form.character)
      return match
        ? match.styles.map(style => ({
          value: style,
          label: STYLE_LABELS[style] || style
        }))
        : [{ value: 'casual-sitting', label: STYLE_LABELS['casual-sitting'] }]
    },
    currentAvatarPreview() {
      return this.avatarCharacters.find(item => item.value === this.form.character) || this.avatarCharacters[0]
    },
    currentAvatarLabel() {
      return `${this.currentAvatarPreview.chineseLabel} / ${this.currentStyleLabel}`
    },
    currentStyleLabel() {
      return STYLE_LABELS[this.form.style] || this.form.style
    },
    currentVoiceLabel() {
      const match = this.voiceOptions.find(item => item.value === this.form.voice)
      return match ? match.label : this.form.voice
    },
    placeholderCardStyle() {
      const accent = this.currentAvatarPreview.accent || '#6aa8ff'
      const bg = this.form.backgroundImage
        ? `linear-gradient(rgba(15, 23, 42, 0.28), rgba(15, 23, 42, 0.45)), url(${this.form.backgroundImage}) center/cover no-repeat`
        : `radial-gradient(circle at top, ${accent}55, transparent 42%), linear-gradient(180deg, ${this.normalizeBackgroundColor(this.form.backgroundColor)}, rgba(15, 23, 42, 0.12))`
      return {
        background: bg
      }
    },
    selectedAvatarVariantKey() {
      return [this.form.character, this.form.style].join('|')
    },
    tokenSourceLabel() {
      if (!this.tokenSource) return '未获取'
      return this.tokenSource === 'env'
        ? '环境变量（备用）'
        : this.tokenSource === 'database'
          ? '数据库 Azure_digital_human'
          : this.tokenSource
    }
  },
  watch: {
    'form.character'(value) {
      const match = this.avatarCharacters.find(item => item.value === value)
      if (match && match.styles.indexOf(this.form.style) === -1) {
        this.form.style = match.styles[0]
      }
    },
    selectedAvatarVariantKey() {
      this.scheduleSessionRefresh('角色或风格')
    }
  },
  mounted() {
    this.bootstrap()
  },
  beforeDestroy() {
    if (this.sessionRefreshTimer) clearTimeout(this.sessionRefreshTimer)
    this.teardownSession()
  },
  methods: {
    async bootstrap() {
      if (!window.RTCPeerConnection) {
        this.lastError = '当前浏览器不支持 WebRTC'
        this.statusText = '浏览器不支持'
        this.appendLog('当前浏览器不支持 WebRTC，无法使用数字人实时会话。', 'error')
        return
      }
      await this.loadRuntimeConfig()
    },
    appendLog(message, type = 'info') {
      this.logs.unshift({
        id: ++this.logSeq,
        type,
        time: formatNowTime(),
        message
      })
      this.logs = this.logs.slice(0, 40)
    },
    clearLogs() {
      this.logs = []
    },
    applyPreset(preset) {
      this.form.text = preset.text
    },
    scheduleSessionRefresh(reason) {
      if (!this.sessionActive || this.startingSession || this.speaking) return
      if (this.sessionRefreshTimer) clearTimeout(this.sessionRefreshTimer)
      this.sessionRefreshTimer = setTimeout(async () => {
        this.appendLog(`检测到${reason}变更，正在自动重建 Avatar 会话。`)
        await this.startSession()
      }, 260)
    },
    async loadRuntimeConfig() {
      try {
        const response = await this.$axios.get('/avatar/runtime-config')
        const data = response.data || {}
        this.serviceReady = !!data.available
        this.serviceRegion = data.speechRegion || ''
        this.tokenSource = data.source || ''
        if (data.available) {
          this.statusText = '服务可用'
          this.appendLog(`已加载 Avatar 服务配置，区域 ${data.speechRegion}。`)
        } else {
          this.statusText = '服务未配置'
          this.lastError = data.message || '后端未配置 Avatar 所需 Azure 凭据'
          this.appendLog(this.lastError, 'error')
        }
      } catch (error) {
        const message = this.extractErrorMessage(error, '读取 Avatar 运行配置失败')
        this.statusText = '配置读取失败'
        this.lastError = message
        this.appendLog(message, 'error')
      }
    },
    ensureSdkReady() {
      const sdk = this.$sdk
      if (!sdk || !sdk.AvatarSynthesizer || !sdk.AvatarConfig || !sdk.SpeechConfig) {
        throw new Error('当前前端 Speech SDK 版本不支持 Avatar，请先执行 npm install 重新安装升级后的依赖。')
      }
      return sdk
    },
    async fetchSessionTokenBundle() {
      const response = await this.$axios.get('/avatar/realtime-token')
      const data = response.data || {}
      if (!data.speechToken || !data.speechRegion || !data.relay) {
        throw new Error(data.message || 'Avatar token 返回不完整')
      }
      this.serviceReady = true
      this.serviceRegion = data.speechRegion
      this.tokenSource = data.source || ''
      return data
    },
    buildAvatarConfig(sdk) {
      const avatarConfig = new sdk.AvatarConfig(this.form.character, this.form.style)
      if (this.form.backgroundImage) avatarConfig.backgroundImage = this.form.backgroundImage
      else avatarConfig.backgroundColor = this.normalizeBackgroundColor(this.form.backgroundColor)
      return avatarConfig
    },
    createPeerConnection(relay) {
      const urls = Array.isArray(relay.urls) ? relay.urls : relay.urls ? [relay.urls] : []
      const peerConnection = new RTCPeerConnection({
        iceServers: [
          {
            urls,
            username: relay.username,
            credential: relay.credential
          }
        ]
      })

      peerConnection.addTransceiver('video', { direction: 'sendrecv' })
      peerConnection.addTransceiver('audio', { direction: 'sendrecv' })

      peerConnection.ontrack = (event) => {
        if (!event.streams || !event.streams[0]) return
        if (event.track.kind === 'video' && this.$refs.avatarVideo) {
          this.$refs.avatarVideo.srcObject = event.streams[0]
          this.hasRemoteVideo = true
          this.$refs.avatarVideo.play().catch(() => {})
          this.appendLog('已收到视频轨。')
        }
        if (event.track.kind === 'audio' && this.$refs.avatarAudio) {
          this.$refs.avatarAudio.srcObject = event.streams[0]
          this.$refs.avatarAudio.play().catch(() => {})
          this.appendLog('已收到音频轨。')
        }
      }

      peerConnection.oniceconnectionstatechange = () => {
        this.iceState = peerConnection.iceConnectionState || 'unknown'
        this.appendLog(`ICE 状态变更为 ${this.iceState}。`)
      }

      peerConnection.onconnectionstatechange = () => {
        this.peerState = peerConnection.connectionState || 'unknown'
        if (this.peerState === 'failed' || this.peerState === 'disconnected' || this.peerState === 'closed') {
          this.sessionReady = false
        }
        this.appendLog(`PeerConnection 状态变更为 ${this.peerState}。`)
      }

      return peerConnection
    },
    async startSession() {
      if (this.sessionPromise) return this.sessionPromise
      this.sessionPromise = this.startSessionInternal()
      try {
        return await this.sessionPromise
      } finally {
        this.sessionPromise = null
      }
    },
    async startSessionInternal() {
      let sdk
      try {
        sdk = this.ensureSdkReady()
        this.startingSession = true
        this.statusText = '正在建立会话'
        this.lastError = ''
        this.appendLog(`开始申请实时会话 token，角色 ${this.currentAvatarPreview.label}，风格 ${this.currentStyleLabel}。`)

        await this.teardownSession()

        const bundle = await this.fetchSessionTokenBundle()
        const speechConfig = sdk.SpeechConfig.fromAuthorizationToken(bundle.speechToken, bundle.speechRegion)
        speechConfig.speechSynthesisVoiceName = this.form.voice

        this.peerConnection = this.createPeerConnection(bundle.relay)
        this.avatarSynthesizer = new sdk.AvatarSynthesizer(
          speechConfig,
          this.buildAvatarConfig(sdk)
        )

        await this.avatarSynthesizer.startAvatarAsync(this.peerConnection)

        this.sessionReady = true
        this.sessionActive = true
        this.statusText = '会话已建立'
        this.appendLog('Avatar 会话建立成功。')
      } catch (error) {
        const message = this.extractErrorMessage(error, '建立 Avatar 会话失败')
        this.lastError = message
        this.statusText = '建立失败'
        this.sessionReady = false
        this.sessionActive = false
        this.appendLog(message, 'error')
        await this.teardownSession()
      } finally {
        this.startingSession = false
      }
    },
    async speakText() {
      const text = (this.form.text || '').trim()
      if (!text) {
        this.$message.warning('请先输入要播报的文本')
        return
      }

      try {
        if (this.startingSession) {
          await this.startSession()
        }
        if (!this.sessionReady || !this.avatarSynthesizer) {
          await this.startSession()
        }
        if (!this.sessionReady || !this.avatarSynthesizer) return

        this.speaking = true
        this.statusText = '数字人播报中'
        this.appendLog(`开始播报文本，长度 ${text.length} 字。`)

        const sdk = this.ensureSdkReady()
        const result = await this.avatarSynthesizer.speakTextAsync(text)
        if (result && result.reason && sdk.ResultReason && result.reason !== sdk.ResultReason.SynthesizingAudioCompleted) {
          throw this.buildCancellationError(sdk, result, '数字人播报失败')
        }

        this.statusText = '播报完成'
        this.appendLog('本次文本播报完成。')
      } catch (error) {
        const message = this.extractErrorMessage(error, '数字人播报失败')
        this.lastError = message
        this.statusText = '播报失败'
        this.appendLog(message, 'error')
      } finally {
        this.speaking = false
      }
    },
    async stopSession() {
      await this.teardownSession()
      this.statusText = '会话已关闭'
      this.appendLog('Avatar 会话已关闭。')
    },
    async teardownSession() {
      if (this.sessionRefreshTimer) {
        clearTimeout(this.sessionRefreshTimer)
        this.sessionRefreshTimer = null
      }
      this.sessionReady = false
      this.sessionActive = false
      this.hasRemoteVideo = false
      this.iceState = 'new'
      this.peerState = '未创建'

      if (this.avatarSynthesizer) {
        try {
          if (typeof this.avatarSynthesizer.stopAvatarAsync === 'function') {
            await this.avatarSynthesizer.stopAvatarAsync()
          } else {
            await this.avatarSynthesizer.close()
          }
        } catch (error) {
          this.appendLog(this.extractErrorMessage(error, '关闭 AvatarSynthesizer 失败'), 'error')
        }
        this.avatarSynthesizer = null
      }

      if (this.peerConnection) {
        try {
          this.peerConnection.getReceivers().forEach(receiver => {
            if (receiver.track) receiver.track.stop()
          })
          this.peerConnection.getSenders().forEach(sender => {
            if (sender.track) sender.track.stop()
          })
          this.peerConnection.close()
        } catch (error) {
          this.appendLog(this.extractErrorMessage(error, '关闭 PeerConnection 失败'), 'error')
        }
        this.peerConnection = null
      }

      if (this.$refs.avatarVideo) this.$refs.avatarVideo.srcObject = null
      if (this.$refs.avatarAudio) this.$refs.avatarAudio.srcObject = null
    },
    buildCancellationError(sdk, result, fallbackMessage) {
      if (sdk.CancellationDetails && typeof sdk.CancellationDetails.fromResult === 'function') {
        const details = sdk.CancellationDetails.fromResult(result)
        const detailText = details && details.errorDetails ? details.errorDetails : ''
        return new Error(detailText || fallbackMessage)
      }
      return new Error(fallbackMessage)
    },
    normalizeBackgroundColor(value) {
      const color = String(value || '').trim().replace('#', '')
      if (/^[0-9a-fA-F]{8}$/.test(color)) return `#${color.toUpperCase()}`
      if (/^[0-9a-fA-F]{6}$/.test(color)) return `#${color.toUpperCase()}FF`
      return '#F3F7FFFF'
    },
    extractErrorMessage(error, fallbackMessage) {
      if (error && error.response && error.response.data) {
        const data = error.response.data
        if (typeof data === 'string') return data
        if (data.message) return data.message
      }
      if (error && error.message) {
        if (String(error.message).includes('websocket error code: 1011')) {
          return 'Avatar 服务内部错误（websocket 1011）。通常是当前角色/风格组合不受支持，或 Azure 侧会话建立失败。请切换到其他官方支持的角色风格后重试。'
        }
        return error.message
      }
      return fallbackMessage
    }
  }
}
</script>

<style lang="less" scoped>
.avatar-page {
  display: flex;
  flex-direction: column;
  gap: 20px;
  color: var(--text-primary);
}

.avatar-hero {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(280px, 0.8fr);
  gap: 18px;
}

.avatar-hero-copy,
.hero-card,
.avatar-panel {
  background:
    radial-gradient(circle at top left, rgba(64, 158, 255, 0.16), transparent 42%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(244, 248, 255, 0.96));
  border: 1px solid var(--border-color);
  border-radius: 22px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.08);
}

.avatar-hero-copy {
  padding: 30px 32px;
}

.avatar-kicker {
  margin: 0 0 8px;
  color: #2c6cf6;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.avatar-title {
  margin: 0;
  font-size: 40px;
  line-height: 1.1;
}

.avatar-subtitle {
  margin: 14px 0 0;
  max-width: 720px;
  line-height: 1.8;
  color: var(--text-regular);
}

.avatar-hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 24px;
}

.avatar-hero-side {
  display: flex;
}

.hero-card {
  width: 100%;
  padding: 24px;
}

.hero-card-top,
.hero-status-row,
.panel-header,
.status-row,
.log-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.hero-card-top {
  margin-bottom: 22px;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(230, 74, 25, 0.12);
  color: #d84315;
  font-weight: 700;
}

.hero-badge.ok {
  background: rgba(46, 125, 50, 0.12);
  color: #2e7d32;
}

.hero-meta {
  color: var(--text-secondary);
}

.hero-status-row + .hero-status-row {
  margin-top: 14px;
}

.hero-status-label {
  color: var(--text-secondary);
}

.avatar-layout {
  display: grid;
  grid-template-columns: minmax(340px, 420px) minmax(0, 1fr);
  gap: 20px;
}

.avatar-panel {
  padding: 24px;
}

.panel-header h2,
.notes-card h3,
.log-card h3 {
  margin: 0;
}

.panel-header p {
  margin: 6px 0 0;
  color: var(--text-secondary);
}

.control-alert {
  margin-top: 18px;
}

.alert-copy {
  line-height: 1.7;
}

.field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: 18px;
}

.field {
  margin-top: 18px;
}

.field label {
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 700;
  color: var(--text-secondary);
}

.color-row {
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr);
  gap: 10px;
}

.quick-preset-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.quick-preset {
  border: 1px solid rgba(44, 108, 246, 0.18);
  background: rgba(44, 108, 246, 0.08);
  color: #2c6cf6;
  border-radius: 999px;
  padding: 8px 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.quick-preset:hover {
  background: rgba(44, 108, 246, 0.14);
  transform: translateY(-1px);
}

.notes-card,
.status-card,
.log-card {
  margin-top: 20px;
  padding: 18px 20px;
  border-radius: 18px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
}

.notes-card ul {
  margin: 12px 0 0;
  padding-left: 18px;
  color: var(--text-regular);
  line-height: 1.8;
}

.doc-links {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 14px;
}

.doc-links a {
  color: #2c6cf6;
  text-decoration: none;
}

.stage-shell {
  margin-top: 18px;
  padding: 18px;
  border-radius: 20px;
  background:
    linear-gradient(145deg, #0f172a, #172554 60%, #1d4ed8 120%);
  color: #fff;
}

.stage-shell.ready {
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.22);
}

.stage-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
}

.stage-pill {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
  font-size: 12px;
  font-weight: 700;
}

.stage-pill.subtle {
  background: rgba(255, 255, 255, 0.12);
  font-weight: 500;
}

.stage {
  position: relative;
  overflow: hidden;
  min-height: 520px;
  border-radius: 16px;
  background:
    radial-gradient(circle at top, rgba(96, 165, 250, 0.22), transparent 36%),
    linear-gradient(180deg, rgba(8, 15, 35, 0.74), rgba(8, 15, 35, 0.98));
}

.avatar-video {
  display: block;
  width: 100%;
  min-height: 520px;
  object-fit: cover;
  background: transparent;
}

.avatar-audio {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.stage-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.placeholder-card {
  width: min(360px, 100%);
  padding: 28px 24px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  background: rgba(15, 23, 42, 0.44);
  backdrop-filter: blur(12px);
  text-align: center;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
}

.placeholder-avatar-shell {
  width: 148px;
  height: 148px;
  margin: 0 auto 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.92), rgba(255, 255, 255, 0.66));
  box-shadow: 0 16px 30px rgba(15, 23, 42, 0.18);
}

.placeholder-avatar-image {
  width: 94px;
  height: 94px;
  object-fit: contain;
}

.placeholder-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
}

.placeholder-style {
  margin: 8px 0 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.84);
}

.placeholder-text {
  margin: 10px 0 0;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.74);
}

.placeholder-tip {
  margin: 14px 0 0;
  color: rgba(255, 255, 255, 0.88);
  font-size: 12px;
}

.status-row + .status-row {
  margin-top: 12px;
}

.status-row span {
  color: var(--text-secondary);
}

.log-list {
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 260px;
  overflow: auto;
}

.log-item {
  display: grid;
  grid-template-columns: 82px minmax(0, 1fr);
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: var(--bg-primary);
  border: 1px solid var(--border-light);
}

.log-item[data-type='error'] {
  border-color: rgba(245, 108, 108, 0.28);
  background: rgba(245, 108, 108, 0.06);
}

.log-time {
  color: var(--text-secondary);
  font-family: Consolas, 'Courier New', monospace;
}

.log-message {
  line-height: 1.6;
  color: var(--text-regular);
}

.log-empty {
  margin: 0;
  color: var(--text-secondary);
}

@media (max-width: 768px) {
  .avatar-hero,
  .avatar-layout,
  .field-grid {
    grid-template-columns: 1fr;
  }

  .avatar-title {
    font-size: 30px;
  }

  .avatar-hero-copy,
  .avatar-panel,
  .hero-card {
    padding: 18px;
  }

  .stage {
    min-height: 360px;
  }

  .avatar-video {
    min-height: 360px;
  }
}
</style>

<style lang="less">
#app.dark-mode {
  .avatar-hero-copy,
  .hero-card,
  .avatar-panel {
    background:
      radial-gradient(circle at top left, rgba(64, 158, 255, 0.16), transparent 42%),
      linear-gradient(180deg, rgba(24, 33, 54, 0.96), rgba(21, 28, 46, 0.96));
    box-shadow: 0 16px 36px rgba(0, 0, 0, 0.22);
  }

  .notes-card,
  .status-card,
  .log-card {
    background: rgba(15, 23, 42, 0.42);
  }

  .quick-preset {
    border-color: rgba(96, 165, 250, 0.24);
    background: rgba(96, 165, 250, 0.1);
    color: #8ab4ff;
  }

  .log-item {
    background: rgba(15, 23, 42, 0.54);
  }
}
</style>
